
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Brain, Speech, Hand, Heart, Users, LucideIcon } from "lucide-react";
import { CATEGORY_NAMES } from "@/utils/categoryMapping";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

interface CategoryCardProps {
  name: string;
  icon: LucideIcon;
  isOpen: boolean;
  onToggle: () => void;
  levels: Array<{ 
    name: string; 
    difficulty: string; 
    title?: string; 
    id: string;
    className?: string;
    completed?: boolean;
  }>;
  onLevelClick: (levelName: string) => void;
}

export const CategoryCard = ({
  name,
  icon: Icon,
  isOpen,
  onToggle,
  levels,
  onLevelClick,
}: CategoryCardProps) => {
  const isMobile = useIsMobile();

  return (
    <Card className="p-4">
      <Collapsible
        open={isOpen}
        onOpenChange={onToggle}
      >
        <CollapsibleTrigger asChild>
          <Button 
            variant="outline"
            className="w-full justify-start gap-3"
          >
            <Icon className="h-5 w-5 text-primary" />
            <span>{name}</span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4 space-y-3">
          {levels.map((level) => (
            <Button 
              key={level.id}
              variant="ghost"
              className={cn(
                "w-full text-left hover:text-foreground hover:bg-[#b3cde0]",
                isMobile ? (
                  "min-h-[80px] px-4 py-3 text-sm whitespace-normal break-words"
                ) : (
                  "justify-start"
                ),
                level.className
              )}
              onClick={() => onLevelClick(level.title || level.name)}
            >
              {level.title || level.name}
            </Button>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export const getCategoryIcon = (category: string): LucideIcon => {
  switch (category) {
    case CATEGORY_NAMES.MOTOR_SKILLS:
      return Hand;
    case CATEGORY_NAMES.LANGUAGE_SKILLS:
      return Speech;
    case CATEGORY_NAMES.COGNITIVE_DEVELOPMENT:
      return Brain;
    case CATEGORY_NAMES.INDEPENDENT_SKILLS:
      return Users;
    case CATEGORY_NAMES.SOCIAL_EMOTIONAL:
      return Heart;
    default:
      return Brain;
  }
};
